import { IUserProfileBond } from '__generated__/api';
import { AxiosResponse } from 'axios';
import * as dynamic from 'utils/dynamic';
import { decoratorIsNotNullable, equals } from 'utils/dynamic';
import {
  API,
  apiRtk,
  makePatchArgs,
  RTK_TAGS,
  rtkAdapterDynamicItem,
  rtkAdapterDynamicToSource,
  rtkAdapterError,
} from 'utils/service';
import { PatchPartial } from 'utils/types';
import {
  GetUserProfileBondAlertsInput,
  GetUserProfileBondAlertsOutput,
  GetUserProfileBondsForProfileInput,
  GetUserProfileBondsForProfileItem,
  IUserProfileBondArg,
  IUserProfileGridBonds,
} from './models';

export * from './models';

const requestGet = API.api.userProfileBondsGetAllDynamicCreate;
const requestPatch = API.api.userProfileBondsPatchPartialUpdate;

export const apiUserProfileBonds = apiRtk.injectEndpoints({
  endpoints: (build) => ({
    getUserProfileGridBonds: build.query<IUserProfileGridBonds, IUserProfileBondArg>({
      queryFn: async ({ take: Take, skip: Skip, orderBy, ...filters }) => {
        try {
          const res = await requestGet({
            Select: dynamic.select(
              'id',
              'bondID',
              'bond.symbol',
              'bond.name',
              'bond.controllingShareHolder',
              'bond.issuerNum',
              'bond.equityTypeID',
              'bond.linkedAssetID',
              'bond.tradingStage',
              'bond.issuerID',
              'bond.branchID',
              'bond.lastRateDate',
              'bond.flowYear1',
              'bond.flowYear2',
              'bond.flowYear3',
              'bond.flowYear4',
              'bond.flowYear5',
              'bond.flowYear6',
              'bond.flowYear7',
              'bond.flowYearElse',
              'bond.issuer.title as issuerName',
              'bond.equityType.title as equityTypeName',
              'bond.branch.title as branchName',
              'bond.baseRate',
              'bond.lastDealRate',
              'bond.baseRateChangePercentage',
              'bond.yesterdayYield',
              'bond.beginOfYearYield',
              'bond.grossDuration',
              'bond.grossYieldToMaturityDeltaByDuration',
              'bond.linkedAsset.title as linkedAssetName',
              'bond.annualInterest',
              'bond.bondMidroogRank',
              'bond.bondMidroogOutlook',
              'bond.bondMaalotRank',
              'bond.bondMaalotOutlook',
              'bond.weekAverageNIS',
              'bond.dailyTurnover',
              'bond.grossAdjustedValue',
              'bond.listedFortune',
              'bond.redemptionOrExpirationDate',
              'bond.issuingDate',
              'bond.grossYieldToMaturity',
              'bond.countryOfIncorporation',
              'bond.collateralSummary',
            ),
            OrderBy: dynamic.orderBy(orderBy),
            Filter: dynamic
              .mergeFilters(
                dynamic.makeFilter('userProfileID', filters.userProfileID, dynamic.equals),
                dynamic.makeFilter(
                  'symbol',
                  filters.bonds,
                  dynamic.decoratorValueArray(dynamic.decoratorStringify(dynamic.equals)),
                ),
                dynamic.makeFilter(
                  'bond.branchID',
                  filters.branchIDs,
                  dynamic.decoratorValueArray(dynamic.equals),
                ),
                dynamic.makeFilter(
                  'bond.linkedAssetID',
                  filters.linkedAssetIDs,
                  dynamic.decoratorValueArray(dynamic.equals),
                ),
                dynamic.makeFilter(
                  'bond.issuerID',
                  filters.issuerIDs,
                  dynamic.decoratorValueArray(dynamic.equals),
                ),
                dynamic.makeFilter(
                  'bond.equityTypeID',
                  filters.equityTypeIDs,
                  dynamic.decoratorValueArray(dynamic.equals),
                ),
                dynamic.makeFilter(
                  'bond.maalotRatingID',
                  filters.maalotRatingIDs,
                  dynamic.decoratorValueArray(dynamic.equals),
                ),
                dynamic.makeFilter(
                  'bond.midroogRatingID',
                  filters.midroogRatingIDs,
                  dynamic.decoratorValueArray(dynamic.equals),
                ),
                dynamic.makeFilter('isActive', filters.isActive, decoratorIsNotNullable(equals)),
              )
              .join('&&'),
            Take,
            Skip,
            Count: true,
          });
          return res as AxiosResponse<IUserProfileGridBonds>;
        } catch (e: unknown) {
          return rtkAdapterError(e);
        }
      },
    }),
    getUserProfileBondAlerts: build.query<
      GetUserProfileBondAlertsOutput,
      GetUserProfileBondAlertsInput
    >({
      queryFn: async ({ userProfileID, bondID }) => {
        try {
          const res = await requestGet({
            Select: dynamic.select(
              'id',
              'bondID',
              'userProfileID',
              'alert1',
              'alert2',
              'alert3',
              'alert4',
              'alert5',
              'alert6',
              'alert7',
              'alert8',
              'bond.symbol',
            ),
            Filter: dynamic
              .mergeFilters(
                dynamic.makeFilter('userProfileID', userProfileID, dynamic.equals),
                dynamic.makeFilter('bondID', bondID, dynamic.equals),
              )
              .join('&&'),
            Take: 1,
          });
          return rtkAdapterDynamicItem<GetUserProfileBondAlertsOutput>(res as unknown as any);
        } catch (e: unknown) {
          return rtkAdapterError(e);
        }
      },
    }),
    getUserProfileBondsForProfile: build.query<
      Array<GetUserProfileBondsForProfileItem>,
      GetUserProfileBondsForProfileInput
    >({
      queryFn: async ({ userProfileID }) => {
        try {
          const res = await requestGet({
            Select: dynamic.select(
              'id',
              'bondID',
              'userProfileID',
              'alert1',
              'alert2',
              'alert3',
              'alert4',
              'alert5',
              'alert6',
              'alert7',
              'alert8',
              'bond.symbol',
              'bond.name',
              'bond.issuerName',
              'bond.issuerID',
              'bond.controllingShareHolder',
              'bond.issuerNum',
              'bond.equityTypeName',
              'bond.branchName',
              'bond.baseRate',
              'bond.lastDealRate',
              'bond.baseRateChangePercentage',
              'bond.yesterdayYield',
              'bond.beginOfYearYield',
              'bond.grossYieldToMaturity',
              'bond.grossDuration',
              'bond.grossYieldToMaturityDeltaByDuration',
              'bond.linkedAssetName',
              'bond.weekAverageNIS',
              'bond.dailyTurnover',
              'bond.annualInterest',
              'bond.grossAdjustedValue',
              'bond.listedFortune',
              'bond.redemptionOrExpirationDate',
              'bond.issuingDate',
              'bond.lastRateDate',
              'bond.tradingStage',
              'bond.bondMidroogRank',
              'bond.bondMidroogOutlook',
              'bond.bondMaalotRank',
              'bond.bondMaalotOutlook',
              'bond.flowYear1',
              'bond.flowYear2',
              'bond.flowYear3',
              'bond.flowYear4',
              'bond.flowYear5',
              'bond.flowYear6',
              'bond.flowYear7',
              'bond.flowYearElse',
            ),
            Filter: dynamic
              .mergeFilters(dynamic.makeFilter('userProfileID', userProfileID, dynamic.equals))
              .join('&&'),
          });
          const { data } = rtkAdapterDynamicToSource<GetUserProfileBondsForProfileItem>(
            res as unknown as any,
          );
          return { data };
        } catch (e: unknown) {
          return rtkAdapterError(e);
        }
      },
      providesTags: [{ type: RTK_TAGS.BONDS_HELPERS, id: 'user-bonds' }],
    }),
    patchUserProfileBondAlert: build.mutation<
      void,
      PatchPartial<IUserProfileBond, 'id' | 'userProfileID' | 'bondID'>
    >({
      queryFn: async (input) => {
        try {
          const { userProfileID, bondID, ...rest } = input;
          await requestPatch(...makePatchArgs(rest));
          return { data: undefined };
        } catch (e) {
          return rtkAdapterError(e as any);
        }
      },
      invalidatesTags: [{ type: RTK_TAGS.BONDS_HELPERS, id: 'user-bonds' }],
    }),
  }),
});
